import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles, TrendingUp, ArrowRight, Check, X, Wand2 } from "lucide-react";
import { PageHeader, Button, StatusBadge, LoadingBlock, ErrorState, EmptyState, toast } from "@/components/ds";
import { api, formatApiError, type OptimizationRec } from "@/lib/api";

const SEV_TONE: Record<string, string> = { high: "red", medium: "amber", low: "slate" };

function RecCard({ r, canApply, onAction }: { r: OptimizationRec; canApply: boolean; onAction: () => void }) {
  const qc = useQueryClient();
  const mut = (fn: () => Promise<any>) => async () => { try { await fn(); onAction(); } catch (e) { toast.error(formatApiError(e)); } };
  const review = useMutation({ mutationFn: () => api.optimizationReview(r.key), onSuccess: () => { toast.success("Marked as reviewed"); qc.invalidateQueries({ queryKey: ["optimization"] }); } });
  const dismiss = useMutation({ mutationFn: () => api.optimizationDismiss(r.key), onSuccess: () => { toast.success("Dismissed"); qc.invalidateQueries({ queryKey: ["optimization"] }); } });
  const apply = useMutation({ mutationFn: () => api.optimizationApply(r.key), onSuccess: (d) => { toast.success(d.note || "Applied"); qc.invalidateQueries({ queryKey: ["optimization"] }); }, onError: (e) => toast.error(formatApiError(e)) });

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`opt-rec-${r.key}`}>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge tone={SEV_TONE[r.severity] as any}>{r.severity}</StatusBadge>
        <StatusBadge tone="slate">{r.category}</StatusBadge>
        {r.status === "reviewed" && <StatusBadge tone="blue">reviewed</StatusBadge>}
        {r.status === "applied" && <StatusBadge tone="green">applied</StatusBadge>}
      </div>
      <p className="mt-2 text-sm font-semibold text-slate-900">{r.title}</p>
      <p className="mt-1 text-sm text-slate-600"><span className="font-medium text-slate-500">Evidence:</span> {r.evidence}</p>
      {r.expected_impact && <p className="mt-1 text-sm text-emerald-700"><span className="font-medium">Expected impact:</span> {r.expected_impact}</p>}
      {r.apply_note && <p className="mt-1 text-xs italic text-slate-400">{r.apply_note}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {r.status !== "applied" && (
          <Button size="sm" variant="secondary" onClick={() => review.mutate()} disabled={review.isPending} data-testid={`opt-review-${r.key}`}><Check className="h-4 w-4" /> Review</Button>
        )}
        {r.apply_supported && canApply && r.status !== "applied" && (
          <Button size="sm" onClick={() => apply.mutate()} disabled={apply.isPending} data-testid={`opt-apply-${r.key}`}><Wand2 className="h-4 w-4" /> Apply</Button>
        )}
        <Link to={r.deep_link}><Button size="sm" variant="ghost" data-testid={`opt-link-${r.key}`}>Open settings <ArrowRight className="h-4 w-4" /></Button></Link>
        {r.status !== "applied" && (
          <Button size="sm" variant="ghost" onClick={() => dismiss.mutate()} disabled={dismiss.isPending} data-testid={`opt-dismiss-${r.key}`}><X className="h-4 w-4" /> Dismiss</Button>
        )}
      </div>
    </div>
  );
}

export default function OptimizationCenter() {
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["optimization"], queryFn: () => api.optimization() });

  const header = (
    <PageHeader title="Optimization Center" testid="opt-header"
      description="Evidence-based ways to improve operations. Nothing changes automatically — you review, apply or dismiss each recommendation." />
  );
  if (isLoading) return <div data-testid="page-optimization" className="space-y-6">{header}<LoadingBlock rows={4} /></div>;
  if (isError || !data) return <div data-testid="page-optimization" className="space-y-6">{header}<ErrorState onRetry={refetch} /></div>;

  const canApply = !!data.entitlements.apply;
  return (
    <div data-testid="page-optimization" className="space-y-6">
      {header}
      <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-700" data-testid="opt-level">
        <Sparkles className="h-4 w-4" /> {data.entitlements.level} · {data.counts.pending} pending recommendation(s)
      </div>
      {data.recommendations.length === 0 ? (
        <EmptyState icon={TrendingUp} title="No recommendations right now"
          description="As your AI handles more calls, ResFil will surface evidence-based ways to improve. Check back soon." />
      ) : (
        <div className="grid gap-4">
          {data.recommendations.map((r) => (
            <RecCard key={r.key} r={r} canApply={canApply} onAction={() => refetch()} />
          ))}
        </div>
      )}
    </div>
  );
}
